import ResponsiveContainer from './ResponsiveContainer.jsx';
import { ChartTooltip, useChartHover } from './ChartTooltip.jsx';
import { linearScale } from './scale.js';
import { SERIES, FONT, LABEL, LABEL_STRONG, softFill } from './chartColors.js';

// Centered "funnel" of the application pipeline. Each stage is a horizontal bar
// whose width is proportional to how many applications EVER reached it (so the
// bars taper down the page). Each bar is split into the share that advanced
// further (solid accent), is still open (soft accent), and died/stalled (down
// colour) — advanced + open + died = entered. Conversion % from the previous
// stage sits between bars; hover a segment for its raw count. Theme-safe.

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
export const SEGMENTS = [
  { key: 'advanced', label: 'advanced', fill: SERIES.accent },
  // T258(4) — the only translucent segment, so the only one that needs the
  // outline to reach 3:1. See SOFT_FLOOR in chartColors.js for the measurements.
  { key: 'open', label: 'open', fill: softFill(SERIES.accent), stroke: SERIES.accent },
  { key: 'died', label: 'died / stalled', fill: SERIES.down },
];

// T340 — `onSelect(stage)` makes a rung openable. The segments were already
// focusable with full aria-labels; what they lacked was somewhere to go.
export default function FunnelChart({ data = [], rowH = 46, ariaLabel = 'Pipeline funnel', onSelect }) {
  const { hover, show, hide } = useChartHover();
  const maxEntered = Math.max(1, ...data.map((f) => f.entered));
  const height = data.length * rowH;

  return (
    <div className="relative">
      <ResponsiveContainer height={height}>
        {(w) => {
          const scaleW = linearScale(maxEntered, w * 0.88);
          return (
            <>
              <svg width={w} height={height} role="group" aria-label={ariaLabel} style={{ display: 'block', fontFamily: FONT }}>
                {data.map((f, i) => {
                  const rowTop = i * rowH;
                  const barW = Math.max(2, scaleW(f.entered));
                  const x0 = (w - barW) / 2;
                  const barY = rowTop + 16;
                  const barH = 16;
                  let segX = x0;
                  return (
                    <g key={f.stage}>
                      {i > 0 && f.fromPrevPct != null && (
                        <text x={w / 2} y={rowTop + 2} fontSize="9" textAnchor="middle" fill={LABEL}>
                          {f.fromPrevPct}% from prev
                        </text>
                      )}
                      {/* label + count pinned to the chart margins so they never
                          overprint when the bar is a thin tail (offer at scale). */}
                      <text x={2} y={rowTop + 12} fontSize="11" fill={LABEL_STRONG}>
                        {cap(f.stage)}
                      </text>
                      <text x={w - 2} y={rowTop + 12} fontSize="11" textAnchor="end" fill={LABEL}>
                        {f.entered}
                      </text>
                      {SEGMENTS.map((seg) => {
                        const v = f[seg.key] || 0;
                        if (v <= 0 || f.entered <= 0) return null;
                        const segW = (v / f.entered) * barW;
                        const x = segX;
                        segX += segW;
                        const content = `${cap(f.stage)} · ${seg.label}: ${v}`;
                        // Richer accessible name: the stage total + conversion are
                        // visual-only otherwise (sighted-only). Fold them in here.
                        const ariaLabel = `${cap(f.stage)} — ${f.entered} entered${f.fromPrevPct != null ? `, ${f.fromPrevPct}% from previous` : ''}; ${seg.label} ${v}`;
                        const enter = () => show(x + segW / 2, barY, content);
                        return (
                          <rect
                            key={seg.key}
                            // T344 — same tween; the funnel re-scales on the
                            // same toggle the bars do.
                            className="chart-tween"
                            x={x}
                            y={barY}
                            width={Math.max(0.5, segW)}
                            height={barH}
                            fill={seg.fill}
                            stroke={seg.stroke}
                            strokeWidth={seg.stroke ? 1 : undefined}
                            tabIndex={0}
                            aria-label={onSelect ? `${ariaLabel} — view applications` : ariaLabel}
                            role={onSelect ? 'button' : undefined}
                            onClick={onSelect ? () => onSelect(f.stage) : undefined}
                            onKeyDown={onSelect ? (ev) => {
                              // A rect is not a button, so Enter and Space have
                              // to be wired by hand or the segment is reachable
                              // by keyboard and not operable by it.
                              if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onSelect(f.stage); }
                            } : undefined}
                            style={{ cursor: onSelect ? 'pointer' : 'default' }}
                            onMouseEnter={enter}
                            onMouseLeave={hide}
                            onFocus={enter}
                            onBlur={hide}
                          />
                        );
                      })}
                    </g>
                  );
                })}
              </svg>
              <ChartTooltip hover={hover} width={w} />
            </>
          );
        }}
      </ResponsiveContainer>
    </div>
  );
}
